import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Typography,
  Switch,
  FormControlLabel
} from '@mui/material';
import { addDays } from 'date-fns';

const CreateChallengeDialog = ({ open, onClose, onCreateChallenge }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [challengeData, setChallengeData] = useState({
    name: '',
    description: '',
    type: 'individual',
    category: 'steps',
    goal: '',
    duration: 'custom',
    startDate: addDays(new Date(), 1).toISOString().split('T')[0],
    endDate: addDays(new Date(), 8).toISOString().split('T')[0],
    isPublic: false,
    instantStart: false,
    max_participants: '',
    max_teams: '',
    team_size_min: '',
    team_size_max: ''
  });

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Validate required fields
      if (!challengeData.name.trim()) {
        throw new Error('Challenge name is required');
      }
      if (!challengeData.description.trim()) {
        throw new Error('Description is required');
      }
      if (!challengeData.goal) {
        throw new Error('Goal is required');
      }
      if (new Date(challengeData.startDate) >= new Date(challengeData.endDate)) {
        throw new Error('End date must be after start date');
      }
      
      // Format data for API (matching backend requirements)
      const apiData = {
        name: challengeData.name.trim(),
        description: challengeData.description.trim(),
        type: challengeData.type,
        category: challengeData.category,
        goal: parseInt(challengeData.goal),
        duration: challengeData.duration,
        startDate: challengeData.startDate,
        endDate: challengeData.endDate,
        isPublic: challengeData.isPublic,
        instant_start: challengeData.instantStart
      };

      // Add optional fields for team challenges
      if (challengeData.type === 'team' || challengeData.type === 'mixed') {
        if (challengeData.max_teams) {
          apiData.max_teams = parseInt(challengeData.max_teams);
        }
        if (challengeData.team_size_min) {
          apiData.team_size_min = parseInt(challengeData.team_size_min);
        }
        if (challengeData.team_size_max) {
          apiData.team_size_max = parseInt(challengeData.team_size_max);
        }
      }

      if (challengeData.max_participants) {
        apiData.max_participants = parseInt(challengeData.max_participants);
      }
      
      await onCreateChallenge(apiData);
      
      // Reset form and close
      resetForm();
      onClose();
    } catch (error) {
      setError(error.message || 'Failed to create challenge');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setChallengeData({
      name: '',
      description: '',
      type: 'individual',
      category: 'steps',
      goal: '',
      duration: 'custom',
      startDate: addDays(new Date(), 1).toISOString().split('T')[0],
      endDate: addDays(new Date(), 8).toISOString().split('T')[0],
      isPublic: false,
      max_participants: '',
      max_teams: '',
      team_size_min: '',
      team_size_max: ''
    });
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const isTeamChallenge = challengeData.type === 'team' || challengeData.type === 'mixed';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create New Challenge</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Challenge Name"
              value={challengeData.name}
              onChange={(e) => setChallengeData({...challengeData, name: e.target.value})}
              required
              error={!challengeData.name.trim()}
              helperText="Give your challenge an engaging name"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={challengeData.description}
              onChange={(e) => setChallengeData({...challengeData, description: e.target.value})}
              multiline
              rows={3}
              required
              error={!challengeData.description.trim()}
              helperText="Describe what participants need to achieve"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Challenge Type</InputLabel>
              <Select
                value={challengeData.type}
                onChange={(e) => setChallengeData({...challengeData, type: e.target.value})}
                label="Challenge Type"
              >
                <MenuItem value="individual">Individual</MenuItem>
                <MenuItem value="team">Team</MenuItem>
                <MenuItem value="mixed">Mixed (Individual & Team)</MenuItem>
                <MenuItem value="points_sabotage">Points & Sabotage</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={challengeData.category}
                onChange={(e) => setChallengeData({...challengeData, category: e.target.value})}
                label="Category"
              >
                <MenuItem value="steps">Steps</MenuItem>
                <MenuItem value="distance">Distance</MenuItem>
                <MenuItem value="duration">Duration</MenuItem>
                <MenuItem value="activities">Activities</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Duration</InputLabel>
              <Select
                value={challengeData.duration}
                onChange={(e) => setChallengeData({...challengeData, duration: e.target.value})}
                label="Duration"
              >
                <MenuItem value="weekend">Weekend</MenuItem>
                <MenuItem value="week">Week</MenuItem>
                <MenuItem value="month">Month</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={`Goal (${challengeData.category === 'steps' ? 'steps' : challengeData.category === 'distance' ? 'km' : 'minutes'})`}
              value={challengeData.goal}
              onChange={(e) => setChallengeData({...challengeData, goal: e.target.value})}
              type="number"
              required
              error={!challengeData.goal}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={challengeData.startDate}
              onChange={(e) => setChallengeData({...challengeData, startDate: e.target.value})}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: new Date().toISOString().split('T')[0]
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={challengeData.endDate}
              onChange={(e) => setChallengeData({...challengeData, endDate: e.target.value})}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: challengeData.startDate
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={challengeData.instantStart}
                  onChange={(e) => setChallengeData({
                    ...challengeData, 
                    instantStart: e.target.checked,
                    startDate: e.target.checked ? new Date().toISOString().split('T')[0] : challengeData.startDate
                  })}
                  color="primary"
                />
              }
              label="Start Challenge Immediately"
            />
            <Typography variant="body2" color="textSecondary" sx={{ ml: 4, mt: 0.5 }}>
              When enabled, the challenge will start immediately instead of waiting for the scheduled start date
            </Typography>
          </Grid>

          {/* Team-specific settings */}
          {isTeamChallenge && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle1" color="primary" gutterBottom>
                  Team Settings
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Maximum Teams"
                  value={challengeData.max_teams}
                  onChange={(e) => setChallengeData({...challengeData, max_teams: e.target.value})}
                  type="number"
                  helperText="Leave empty for unlimited"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Maximum Participants"
                  value={challengeData.max_participants}
                  onChange={(e) => setChallengeData({...challengeData, max_participants: e.target.value})}
                  type="number"
                  helperText="Total participants across all teams"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Minimum Team Size"
                  value={challengeData.team_size_min}
                  onChange={(e) => setChallengeData({...challengeData, team_size_min: e.target.value})}
                  type="number"
                  helperText="Minimum members per team"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Maximum Team Size"
                  value={challengeData.team_size_max}
                  onChange={(e) => setChallengeData({...challengeData, team_size_max: e.target.value})}
                  type="number"
                  helperText="Maximum members per team"
                />
              </Grid>
            </>
          )}

          {!isTeamChallenge && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Maximum Participants"
                value={challengeData.max_participants}
                onChange={(e) => setChallengeData({...challengeData, max_participants: e.target.value})}
                type="number"
                helperText="Leave empty for unlimited"
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create Challenge'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateChallengeDialog;